import { z } from "zod";
import type { Agent, AgentOutputRequirements } from "@/types/aiToolkit";

const outputRequirementsSchema = z.object({
  format: z.enum(["text", "markdown", "json", "list"]).optional(),
  instructions: z.string().optional(),
  requiredSections: z.array(z.string()).optional(),
  maxLengthHint: z.string().optional(),
});

const personaSchema = z.object({
  role: z.string(),
  expertise: z.array(z.string()),
  communicationStyle: z.string(),
  systemPrompt: z.string().min(1, "System prompt is required"),
});

export const agentBuildSchema = z.object({
  name: z.string().min(1, "Agent name is required"),
  description: z.string().optional(),
  persona: personaSchema,
  outputRequirements: outputRequirementsSchema.optional(),
});

export type AgentBuildValidation = {
  valid: boolean;
  errors: string[];
  warnings: string[];
};

export function validateAgentBuild(agent: Partial<Agent>, requireTrainingContext = false): AgentBuildValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  const parsed = agentBuildSchema.safeParse({
    name: agent.name?.trim() || "",
    description: agent.description,
    persona: agent.persona,
    outputRequirements: agent.outputRequirements,
  });

  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      errors.push(issue.message);
    }
  }

  const trainingCount =
    (agent.trainingDocumentIds?.length ?? 0) + (agent.linkedSourceIds?.length ?? 0);
  if (requireTrainingContext && trainingCount === 0) {
    warnings.push("No training documents or sources linked. The agent may lack reference context.");
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

export function defaultOutputRequirements(): AgentOutputRequirements {
  return { format: "markdown", instructions: "", requiredSections: [] };
}
